import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import '../styles/Recipe.css';

// ── AUTH ──────────────────────────────────────────────────

function LoginPrompt({ onLogin }) {
  const [pw, setPw] = useState('');
  const [err, setErr] = useState('');
  const submit = async (e) => {
    e.preventDefault();
    try {
      const r = await axios.post('/api/auth/login', { password: pw });
      localStorage.setItem('token', r.data.token);
      onLogin(r.data.token);
    } catch { setErr('密码错误'); }
  };
  return (
    <div className="recipe-login">
      <form onSubmit={submit}>
        <h2>菜谱</h2>
        <input type="password" placeholder="密码" value={pw}
          onChange={e => setPw(e.target.value)} autoFocus />
        {err && <p className="recipe-error-msg">{err}</p>}
        <button type="submit" className="btn btn-primary">进入</button>
      </form>
    </div>
  );
}

// ── ITEM LIST EDITOR (steps / notes) ──────────────────────

function ItemListEditor({ items, onChange, placeholder, addLabel, numbered }) {
  const listRef = useRef(null);

  const update = (i, val) => {
    const next = [...items];
    next[i] = val;
    onChange(next);
  };

  const remove = (i) => {
    const next = items.filter((_, j) => j !== i);
    onChange(next.length ? next : ['']);
  };

  const focusInputAt = (idx) => {
    requestAnimationFrame(() => {
      const inputs = listRef.current?.querySelectorAll('input');
      inputs?.[idx]?.focus();
    });
  };

  const addAtEnd = () => {
    const next = [...items, ''];
    onChange(next);
    focusInputAt(next.length - 1);
  };

  const handleKey = (e, i) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      const next = [...items];
      next.splice(i + 1, 0, '');
      onChange(next);
      focusInputAt(i + 1);
    } else if (e.key === 'Backspace' && !items[i] && items.length > 1) {
      e.preventDefault();
      remove(i);
      focusInputAt(Math.max(0, i - 1));
    }
  };

  return (
    <div className="recipe-form-items" ref={listRef}>
      {items.map((item, i) => (
        <div key={i} className="recipe-form-item">
          {numbered && <span className="recipe-form-item-num">{i + 1}.</span>}
          <input
            type="text"
            className="recipe-form-item-input"
            value={item}
            placeholder={placeholder}
            onChange={e => update(i, e.target.value)}
            onKeyDown={e => handleKey(e, i)}
          />
          <button type="button" className="recipe-form-item-del" onClick={() => remove(i)}>×</button>
        </div>
      ))}
      <button type="button" className="ing-btn recipe-form-item-add" onClick={addAtEnd}>
        ＋ {addLabel}
      </button>
    </div>
  );
}

// ── STOCK INDICATOR (3-cell tap-to-fill) ──────────────────

function stockLevel(s) {
  const n = parseInt(s, 10);
  return Number.isNaN(n) ? 0 : Math.max(0, Math.min(3, n));
}

function StockIndicator({ value, onChange }) {
  const level = stockLevel(value);
  const click = (i) => {
    // i is 0-indexed; clicking the top filled cell decrements, otherwise set to i+1
    const next = (i + 1 === level) ? i : i + 1;
    onChange(String(next));
  };
  return (
    <div className="stock-indicator" onClick={e => e.stopPropagation()}>
      {[0, 1, 2].map(i => (
        <button
          key={i}
          type="button"
          className={`stock-cell${i < level ? ' filled' : ''}`}
          onClick={() => click(i)}
          aria-label={`库存 ${i + 1}`}
        />
      ))}
    </div>
  );
}

// ── INGREDIENT POPUP ──────────────────────────────────────

function IngredientPopup({ ing, columns, pos, onClose }) {
  useEffect(() => {
    const h = (e) => { if (!e.target.closest('.ing-popup')) onClose(); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [onClose]);

  const style = {
    left: Math.min(pos.x, window.innerWidth - 210),
    top: Math.min(pos.y, window.innerHeight - 200),
  };

  return (
    <div className="ing-popup" style={style}>
      <button className="ing-popup-close" onClick={onClose}>✕</button>
      <div className="ing-popup-name">{ing.name}</div>
      <div className="ing-popup-row">
        <span className="ing-popup-label">库存</span>
        <span className="ing-popup-stock">
          {[0, 1, 2].map(i => (
            <span key={i} className={`stock-cell${i < stockLevel(ing.stock) ? ' filled' : ''}`} />
          ))}
        </span>
      </div>
      {columns.map(col => (
        ing[col.key] ? (
          <div key={col.key} className="ing-popup-row">
            <span className="ing-popup-label">{col.label}</span>
            <span>{ing[col.key]}</span>
          </div>
        ) : null
      ))}
    </div>
  );
}

// ── INGREDIENT TABLE ──────────────────────────────────────

function IngredientTable({ ingData, onChange }) {
  const { columns, items } = ingData;
  const [editCell, setEditCell] = useState(null); // {id, key}
  const [editVal, setEditVal] = useState('');
  const [addColName, setAddColName] = useState('');
  const [showAddCol, setShowAddCol] = useState(false);
  const [newIngName, setNewIngName] = useState(null);

  const startEdit = (id, key, val) => {
    setEditCell({ id, key });
    setEditVal(val || '');
  };

  const commitCell = () => {
    if (!editCell) return;
    const newItems = items.map(it =>
      it.id === editCell.id ? { ...it, [editCell.key]: editVal } : it
    );
    onChange({ columns, items: newItems });
    setEditCell(null);
  };

  const cellKeyDown = (e) => {
    if (e.key === 'Enter') commitCell();
    if (e.key === 'Escape') setEditCell(null);
  };

  const addColumn = () => {
    const label = addColName.trim();
    if (!label) return;
    const key = 'col_' + Date.now();
    onChange({
      columns: [...columns, { key, label }],
      items: items.map(it => ({ ...it, [key]: '' })),
    });
    setAddColName('');
    setShowAddCol(false);
  };

  const removeColumn = (key) => {
    const newItems = items.map(it => {
      const copy = { ...it };
      delete copy[key];
      return copy;
    });
    onChange({ columns: columns.filter(c => c.key !== key), items: newItems });
  };

  const addIngredient = () => {
    if (!newIngName?.trim()) return;
    const newItem = { id: Date.now(), name: newIngName.trim(), stock: '' };
    columns.forEach(c => { newItem[c.key] = ''; });
    onChange({ columns, items: [...items, newItem] });
    setNewIngName(null);
  };

  const removeIngredient = (id) => {
    onChange({ columns, items: items.filter(it => it.id !== id) });
  };

  const setStock = (id, val) => {
    onChange({ columns, items: items.map(it => it.id === id ? { ...it, stock: val } : it) });
  };

  const EditableCell = ({ id, colKey, value }) => {
    const isEditing = editCell?.id === id && editCell?.key === colKey;
    return isEditing ? (
      <input
        className="ing-cell-input"
        value={editVal}
        onChange={e => setEditVal(e.target.value)}
        onBlur={commitCell}
        onKeyDown={cellKeyDown}
        autoFocus
      />
    ) : (
      <span className="ing-cell" onClick={() => startEdit(id, colKey, value)}>
        {value || <em className="ing-empty">—</em>}
      </span>
    );
  };

  return (
    <div className="ing-table-wrap">
      <div className="ing-table-toolbar">
        <span className="panel-title">食材库</span>
        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
          {showAddCol ? (
            <>
              <input
                className="ing-col-input"
                placeholder="列名"
                value={addColName}
                onChange={e => setAddColName(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') addColumn();
                  if (e.key === 'Escape') setShowAddCol(false);
                }}
                autoFocus
              />
              <button className="ing-btn" onClick={addColumn}>✓</button>
              <button className="ing-btn" onClick={() => setShowAddCol(false)}>✕</button>
            </>
          ) : (
            <button className="ing-btn" onClick={() => setShowAddCol(true)}>＋列</button>
          )}
          <button className="ing-btn ing-btn-accent" onClick={() => setNewIngName('')}>＋食材</button>
        </div>
      </div>

      <div className="ing-table-scroll">
        <table className="ing-table">
          <thead>
            <tr>
              <th className="ing-th-name">名称</th>
              <th className="ing-th-stock">库存</th>
              {columns.map(col => (
                <th key={col.key}>
                  {col.label}
                  <button className="ing-col-del" onClick={() => removeColumn(col.key)} title="删除此列">×</button>
                </th>
              ))}
              <th className="ing-th-del" />
            </tr>
          </thead>
          <tbody>
            {items.map(it => (
              <tr key={it.id}>
                <td className="ing-td-name">
                  <EditableCell id={it.id} colKey="name" value={it.name} />
                </td>
                <td className="ing-td-stock">
                  <StockIndicator value={it.stock} onChange={v => setStock(it.id, v)} />
                </td>
                {columns.map(col => (
                  <td key={col.key}>
                    <EditableCell id={it.id} colKey={col.key} value={it[col.key]} />
                  </td>
                ))}
                <td>
                  <button className="ing-row-del" onClick={() => removeIngredient(it.id)}>×</button>
                </td>
              </tr>
            ))}
            {newIngName !== null && (
              <tr className="ing-new-row">
                <td colSpan={columns.length + 3}>
                  <div style={{ display: 'flex', gap: '0.5rem', padding: '0.25rem' }}>
                    <input
                      className="ing-cell-input"
                      placeholder="食材名称"
                      value={newIngName}
                      onChange={e => setNewIngName(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') addIngredient();
                        if (e.key === 'Escape') setNewIngName(null);
                      }}
                      autoFocus
                    />
                    <button className="ing-btn" onClick={addIngredient}>添加</button>
                    <button className="ing-btn" onClick={() => setNewIngName(null)}>取消</button>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── RECIPE FORM (add / edit) ──────────────────────────────

function RecipeForm({ recipe, ingredients, onSave, onCancel }) {
  const isNew = !recipe.id;
  const [title, setTitle] = useState(recipe.title || '');
  const [steps, setSteps] = useState(() =>
    Array.isArray(recipe.steps) && recipe.steps.length ? [...recipe.steps] : ['']
  );
  const [notes, setNotes] = useState(() => {
    if (Array.isArray(recipe.notes)) return recipe.notes.length ? [...recipe.notes] : [''];
    if (typeof recipe.notes === 'string' && recipe.notes) return [recipe.notes];
    return [''];
  });
  const [ings, setIngs] = useState(recipe.ingredients || []);
  const [images, setImages] = useState(recipe.images || []);
  const [selIngId, setSelIngId] = useState('');
  const [selAmount, setSelAmount] = useState('');
  const [selOptional, setSelOptional] = useState(false);

  const ingMap = Object.fromEntries(ingredients.map(i => [i.id, i]));

  const addIng = () => {
    if (!selIngId) return;
    setIngs(prev => [...prev, {
      ingredientId: Number(selIngId),
      amount: selAmount,
      optional: selOptional,
    }]);
    setSelIngId('');
    setSelAmount('');
    setSelOptional(false);
  };

  const handleImage = (e) => {
    const files = Array.from(e.target.files).slice(0, 2 - images.length);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) =>
        setImages(prev => [...prev, { name: file.name, data: ev.target.result }].slice(0, 2));
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const save = () => {
    if (!title.trim()) return;
    onSave({
      ...recipe,
      id: recipe.id || Date.now(),
      title: title.trim(),
      ingredients: ings,
      steps: steps.map(s => s.trim()).filter(Boolean),
      notes: notes.map(n => n.trim()).filter(Boolean),
      images,
      createdAt: recipe.createdAt || new Date().toISOString().slice(0, 10),
    });
  };

  return (
    <div className="recipe-form">
      <input
        className="recipe-form-title-input"
        placeholder="菜谱名称"
        value={title}
        onChange={e => setTitle(e.target.value)}
        autoFocus={isNew}
      />

      <div className="recipe-form-section">
        <div className="recipe-form-label">食材</div>
        <div className="recipe-form-ing-list">
          {ings.map((ri, idx) => (
            <span key={idx} className={`ing-chip${ri.optional ? ' optional' : ''}`}>
              {ingMap[ri.ingredientId]?.name || '未知'} · {ri.amount}
              {ri.optional && ' (可选)'}
              <button className="ing-chip-del" onClick={() =>
                setIngs(prev => prev.filter((_, i) => i !== idx))
              }>×</button>
            </span>
          ))}
        </div>
        <div className="recipe-form-ing-add">
          <select value={selIngId} onChange={e => setSelIngId(e.target.value)} className="ing-select">
            <option value="">选择食材</option>
            {ingredients.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
          </select>
          <input
            className="ing-amount-input"
            placeholder="用量"
            value={selAmount}
            onChange={e => setSelAmount(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') addIng(); }}
          />
          <label className="ing-optional-label">
            <input type="checkbox" checked={selOptional}
              onChange={e => setSelOptional(e.target.checked)} />
            可选
          </label>
          <button className="ing-btn" onClick={addIng}>添加</button>
        </div>
      </div>

      <div className="recipe-form-section">
        <div className="recipe-form-label">步骤</div>
        <ItemListEditor
          items={steps}
          onChange={setSteps}
          placeholder="步骤内容"
          addLabel="添加步骤"
          numbered
        />
      </div>

      <div className="recipe-form-section">
        <div className="recipe-form-label">备注 / 注意事项</div>
        <ItemListEditor
          items={notes}
          onChange={setNotes}
          placeholder="小技巧、替换食材、注意事项…"
          addLabel="添加备注"
        />
      </div>

      <div className="recipe-form-section">
        <div className="recipe-form-label">图片（最多2张）</div>
        <div className="recipe-form-images">
          {images.map((img, i) => (
            <div key={i} className="recipe-form-img-wrap">
              <img src={img.data} alt={img.name} className="recipe-form-img" />
              <button className="recipe-form-img-del"
                onClick={() => setImages(prev => prev.filter((_, j) => j !== i))}>×</button>
            </div>
          ))}
          {images.length < 2 && (
            <label className="recipe-form-img-upload">
              <input type="file" accept="image/*" style={{ display: 'none' }}
                onChange={handleImage} />
              ＋ 图片
            </label>
          )}
        </div>
      </div>

      <div className="recipe-form-actions">
        <button className="btn btn-primary" onClick={save}>保存</button>
        <button className="ing-btn" onClick={onCancel}>取消</button>
      </div>
    </div>
  );
}

// ── RECIPE CARD ───────────────────────────────────────────

function RecipeCard({ recipe, ingData, canMakeFilter, onEdit, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const [popup, setPopup] = useState(null); // {ing, x, y}
  const { columns, items } = ingData;
  const ingMap = Object.fromEntries(items.map(i => [i.id, i]));

  const stocked = (ri) => {
    const ing = ingMap[ri.ingredientId];
    return ing && stockLevel(ing.stock) > 0;
  };

  const allRequired = recipe.ingredients.filter(ri => !ri.optional).every(stocked);
  if (canMakeFilter && !allRequired) return null;

  const missing = recipe.ingredients
    .filter(ri => !ri.optional && !stocked(ri))
    .map(ri => ingMap[ri.ingredientId]?.name)
    .filter(Boolean);

  const notesArr = Array.isArray(recipe.notes)
    ? recipe.notes
    : (typeof recipe.notes === 'string' && recipe.notes ? [recipe.notes] : []);

  const chipClick = (e, ing) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setPopup({
      ing,
      x: Math.min(rect.left, window.innerWidth - 210),
      y: Math.min(rect.bottom + 6, window.innerHeight - 200),
    });
  };

  return (
    <div className={`recipe-card${allRequired ? ' can-make' : ''}`}>
      {recipe.images?.[0] && (
        <img src={recipe.images[0].data} alt={recipe.title} className="recipe-card-cover" />
      )}
      <div className="recipe-card-body">
        <div className="recipe-card-header">
          <h3 className="recipe-card-title">{recipe.title}</h3>
          <div className="recipe-card-actions">
            <button className="ing-btn" onClick={() => onEdit(recipe)}>编辑</button>
            <button className="ing-btn ing-btn-danger" onClick={() => onDelete(recipe.id)}>删除</button>
          </div>
        </div>

        <div className="recipe-card-chips">
          {recipe.ingredients.map((ri, idx) => {
            const ing = ingMap[ri.ingredientId];
            if (!ing) return null;
            const ok = stocked(ri);
            const cls = ri.optional ? ' optional' : ok ? ' in-stock' : ' missing';
            return (
              <span
                key={idx}
                className={`ing-chip${cls}`}
                title={ri.amount + (ri.optional ? ' · 可选' : '')}
                onClick={e => chipClick(e, ing)}
              >
                {ing.name}
              </span>
            );
          })}
        </div>

        {missing.length > 0 && (
          <div className="recipe-missing-note">缺：{missing.join('、')}</div>
        )}

        <button className="recipe-expand-btn" onClick={() => setExpanded(x => !x)}>
          {expanded ? '收起 ▲' : '展开步骤 ▼'}
        </button>

        {expanded && (
          <div className="recipe-steps">
            <ol>
              {recipe.steps.map((step, i) => <li key={i}>{step}</li>)}
            </ol>
            {notesArr.length > 0 && (
              <div className="recipe-notes">
                <span className="recipe-notes-label">备注</span>
                <ul className="recipe-notes-list">
                  {notesArr.map((n, i) => <li key={i}>{n}</li>)}
                </ul>
              </div>
            )}
            {recipe.images?.length > 1 && (
              <img src={recipe.images[1].data} alt="步骤图" className="recipe-step-img" />
            )}
          </div>
        )}
      </div>

      {popup && (
        <IngredientPopup
          ing={popup.ing}
          columns={columns}
          pos={{ x: popup.x, y: popup.y }}
          onClose={() => setPopup(null)}
        />
      )}
    </div>
  );
}

// ── MAIN PAGE ─────────────────────────────────────────────

function RecipePage({ token }) {
  const authH = useCallback(() => ({
    headers: { Authorization: `Bearer ${token}` },
  }), [token]);

  const [ingData, setIngData] = useState({ columns: [], items: [] });
  const [recipes, setRecipes] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [canMakeFilter, setCanMakeFilter] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState(null);
  const ingSaveTimer = useRef(null);
  const recSaveTimer = useRef(null);

  useEffect(() => {
    Promise.all([
      axios.get('/api/cook/ingredients', authH()),
      axios.get('/api/cook/recipes', authH()),
    ]).then(([ir, rr]) => {
      setIngData(ir.data || { columns: [], items: [] });
      setRecipes(rr.data || []);
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, [authH]);

  const saveIngredients = useCallback((data) => {
    clearTimeout(ingSaveTimer.current);
    ingSaveTimer.current = setTimeout(() => {
      axios.put('/api/cook/ingredients', data, authH()).catch(() => {});
    }, 1500);
  }, [authH]);

  const saveRecipes = useCallback((data) => {
    clearTimeout(recSaveTimer.current);
    recSaveTimer.current = setTimeout(() => {
      axios.put('/api/cook/recipes', data, authH()).catch(() => {});
    }, 1500);
  }, [authH]);

  const handleIngChange = useCallback((data) => {
    setIngData(data);
    saveIngredients(data);
  }, [saveIngredients]);

  const handleSaveRecipe = useCallback((recipe) => {
    setRecipes(prev => {
      const idx = prev.findIndex(r => r.id === recipe.id);
      const next = idx >= 0
        ? prev.map(r => r.id === recipe.id ? recipe : r)
        : [...prev, recipe];
      saveRecipes(next);
      return next;
    });
    setEditingRecipe(null);
  }, [saveRecipes]);

  const handleDeleteRecipe = useCallback((id) => {
    setRecipes(prev => {
      const next = prev.filter(r => r.id !== id);
      saveRecipes(next);
      return next;
    });
  }, [saveRecipes]);

  const canMakeCount = recipes.filter(r =>
    r.ingredients.filter(ri => !ri.optional).every(ri => {
      const ing = ingData.items.find(i => i.id === ri.ingredientId);
      return ing && stockLevel(ing.stock) > 0;
    })
  ).length;

  if (!loaded) return <div className="recipe-loading">加载中…</div>;

  return (
    <div className="recipe-page">
      <header className="recipe-header">
        <h1 className="recipe-title-main">菜谱</h1>
        <div className="recipe-header-actions">
          <button
            className={`recipe-can-make-btn${canMakeFilter ? ' active' : ''}`}
            onClick={() => setCanMakeFilter(x => !x)}
          >
            今天能做什么{canMakeCount > 0 ? ` (${canMakeCount})` : ''}
          </button>
          <button className="ing-btn ing-btn-accent" onClick={() => setEditingRecipe({})}>
            ＋ 添加菜谱
          </button>
        </div>
      </header>

      <div className="recipe-layout">
        <div className="recipe-left">
          <IngredientTable ingData={ingData} onChange={handleIngChange} />
        </div>

        <div className="recipe-right">
          {editingRecipe && (
            <RecipeForm
              recipe={editingRecipe}
              ingredients={ingData.items}
              onSave={handleSaveRecipe}
              onCancel={() => setEditingRecipe(null)}
            />
          )}

          <div className="recipe-cards">
            {recipes.map(r => (
              <RecipeCard
                key={r.id}
                recipe={r}
                ingData={ingData}
                canMakeFilter={canMakeFilter}
                onEdit={setEditingRecipe}
                onDelete={handleDeleteRecipe}
              />
            ))}
            {recipes.length === 0 && !editingRecipe && (
              <div className="recipe-empty">还没有菜谱，点击「＋ 添加菜谱」开始记录</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Recipe() {
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  if (!token) return <LoginPrompt onLogin={setToken} />;
  return <RecipePage token={token} />;
}
